import {
  createInactivityClock,
  createResumableUpload,
} from '@/shared-utilities';

import type { CameraError } from './project-usability-camera.helpers';

const RECORDING_CHUNK_DURATION_SECONDS = 4;
export const RECORDING_CHUNK_DURATION_MS =
  RECORDING_CHUNK_DURATION_SECONDS * 1000;

export interface RecordingSnapshot {
  isRecording: boolean;
  isPaused: boolean;
  isPermissionDenied: boolean;
  isScreenShareMuted: boolean;
  isRequestingPermission: boolean;
  isFinalizing: boolean;
  isUploadStalled: boolean;
  isCameraActive: boolean;
  isCameraOn: boolean;
  cameraStream: MediaStream | null;
  cameraDeviceId: string | null;
  cameraError: CameraError | null;
}

export const INITIAL_RECORDING_SNAPSHOT: RecordingSnapshot = {
  isRecording: false,
  isPaused: false,
  isPermissionDenied: false,
  isScreenShareMuted: false,
  isRequestingPermission: false,
  isFinalizing: false,
  isUploadStalled: false,
  isCameraActive: false,
  isCameraOn: false,
  cameraStream: null,
  cameraDeviceId: null,
  cameraError: null,
};

const RECORDING_SNAPSHOT_READERS: Record<
  keyof RecordingSnapshot,
  (snapshot: RecordingSnapshot) => unknown
> = {
  isRecording: (snapshot) => snapshot.isRecording,
  isPaused: (snapshot) => snapshot.isPaused,
  isPermissionDenied: (snapshot) => snapshot.isPermissionDenied,
  isScreenShareMuted: (snapshot) => snapshot.isScreenShareMuted,
  isRequestingPermission: (snapshot) => snapshot.isRequestingPermission,
  isFinalizing: (snapshot) => snapshot.isFinalizing,
  isUploadStalled: (snapshot) => snapshot.isUploadStalled,
  isCameraActive: (snapshot) => snapshot.isCameraActive,
  isCameraOn: (snapshot) => snapshot.isCameraOn,
  cameraStream: (snapshot) => snapshot.cameraStream,
  cameraDeviceId: (snapshot) => snapshot.cameraDeviceId,
  cameraError: (snapshot) => snapshot.cameraError,
};

interface PendingChunk {
  chunk: Blob;
  onUploadComplete?: (uploadedBytes?: number) => void;
}

function createRecordingStateManager() {
  let snapshot = INITIAL_RECORDING_SNAPSHOT;
  const listeners = new Set<() => void>();

  let uploadRef: ReturnType<typeof createResumableUpload> | null = null;
  let pendingChunks: PendingChunk[] = [];

  let recorder: MediaRecorder | null = null;
  let screenStream: MediaStream | null = null;
  let micStream: MediaStream | null = null;
  let cameraGeneration = 0;
  const pauseReasons = new Set<string>();

  let recordingStartTime: number | null = null;
  let exactDuration: number | null = null;
  let pausedTime = 0;
  let pauseStartTime: number | null = null;
  let confirmedChunkCount = 0;
  let totalChunkCount = 0;
  const inactivityClock = createInactivityClock();

  // Monotonic peak so the saving-modal % never regresses when late chunks
  // grow the denominator after recorder.stop().
  let peakProgress: number | null = null;

  let retryOrchestrator: (() => Promise<void>) | null = null;
  let isRetrying = false;

  const setState = (patch: Partial<RecordingSnapshot>) => {
    const next = { ...snapshot, ...patch };
    const changed = Object.values(RECORDING_SNAPSHOT_READERS).some(
      (read) => read(snapshot) !== read(next)
    );
    if (!changed) return;
    snapshot = next;
    listeners.forEach((listener) => listener());
  };

  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };

  // Hand every queued chunk to the upload module WITHOUT awaiting each PUT —
  // awaiting fell behind MediaRecorder's 4s chunk cadence on weak networks and
  // stranded the tail here. The upload module preserves arrival order itself.
  const flushBuffer = () => {
    if (!uploadRef) return;
    while (pendingChunks.length > 0) {
      const next = pendingChunks.shift();
      if (next && uploadRef) {
        void uploadRef.uploadChunk(next.chunk, next.onUploadComplete);
      }
    }
  };

  const stopCameraTracks = () => {
    cameraGeneration += 1;
    const current = snapshot.cameraStream;
    if (current) current.getTracks().forEach((track) => track.stop());
    setState({ cameraStream: null, isCameraOn: false });
  };

  const cleanup = () => {
    uploadRef = null;
    pendingChunks = [];
    pauseReasons.clear();
    retryOrchestrator = null;
    isRetrying = false;
    stopCameraTracks();
    setState({
      isRecording: false,
      isPaused: false,
      isFinalizing: false,
      isScreenShareMuted: false,
      isUploadStalled: false,
      isCameraActive: false,
      cameraError: null,
    });

    if (screenStream) screenStream.getTracks().forEach((track) => track.stop());
    if (micStream) micStream.getTracks().forEach((track) => track.stop());

    recorder = null;
    screenStream = null;
    micStream = null;
  };

  const reset = () => {
    cleanup();
    inactivityClock.reset();
    recordingStartTime = null;
    exactDuration = null;
    pausedTime = 0;
    pauseStartTime = null;
    confirmedChunkCount = 0;
    totalChunkCount = 0;
    peakProgress = null;
  };

  // Handing the local backlog over before delegating is what keeps the tail
  // out of the "finalized without the last chunks" failure mode (CU-868g960v2).
  const finalize = async () => {
    const upload = uploadRef;
    if (!upload) return;
    flushBuffer();
    return upload.finalize();
  };

  return {
    subscribe,
    getSnapshot: () => snapshot,

    setRecordingState(recording: boolean) {
      setState({ isRecording: recording });
    },

    setPermissionDenied(denied: boolean) {
      setState({ isPermissionDenied: denied });
    },

    setScreenShareMuted(muted: boolean) {
      setState({ isScreenShareMuted: muted });
    },

    setRequestingPermission(requesting: boolean) {
      setState({ isRequestingPermission: requesting });
    },

    setFinalizingState(finalizing: boolean) {
      setState({ isFinalizing: finalizing });
    },

    setStalledState(stalled: boolean) {
      setState({ isUploadStalled: stalled });
    },

    setRetryOrchestrator(fn: (() => Promise<void>) | null) {
      retryOrchestrator = fn;
    },

    // Single-flight: a double-click must not start two parallel retry/settle
    // chains (racing finalize(), double onRecordingComplete + reset).
    async retryUpload(): Promise<void> {
      if (isRetrying || !retryOrchestrator) return;
      isRetrying = true;
      try {
        await retryOrchestrator();
      } finally {
        isRetrying = false;
      }
    },

    hasUpload: () => uploadRef !== null,
    isUploadStuck: () => uploadRef?.isStuck() ?? false,
    uploadHasError: () => uploadRef?.hasError() ?? false,

    async retryStuckUpload(): Promise<void> {
      if (uploadRef) {
        await uploadRef.retryStuck();
      }
    },

    startClock() {
      recordingStartTime = Date.now();
      exactDuration = null;
      pausedTime = 0;
      pauseStartTime = null;
      confirmedChunkCount = 0;
      totalChunkCount = 0;
      peakProgress = null;
    },

    stopClock() {
      if (!recordingStartTime) return;
      const recordingEndTime = Date.now();
      const totalElapsed = recordingEndTime - recordingStartTime;
      const finalPausedTime =
        pauseStartTime && snapshot.isPaused
          ? recordingEndTime - pauseStartTime
          : 0;
      exactDuration = (totalElapsed - (pausedTime + finalPausedTime)) / 1000;
      pausedTime = 0;
      pauseStartTime = null;
    },

    getExactDuration: () => exactDuration,

    getCurrentRecordingTime(): number {
      if (!recordingStartTime || !snapshot.isRecording) return 0;
      const currentTime = Date.now();
      const totalElapsed = currentTime - recordingStartTime;
      const currentPausedTime =
        pauseStartTime && snapshot.isPaused ? currentTime - pauseStartTime : 0;
      return Math.floor(
        (totalElapsed - (pausedTime + currentPausedTime)) / 1000
      );
    },

    confirmChunkUploaded() {
      confirmedChunkCount += 1;
    },

    // Approximation from confirmed uploads: each chunk holds RECORDING_CHUNK_DURATION_SECONDS of video.
    getConfirmedDuration(): number {
      if (confirmedChunkCount === 0 && uploadRef) {
        return 1;
      }
      return confirmedChunkCount * RECORDING_CHUNK_DURATION_SECONDS;
    },

    // Byte-based, monotonic non-decreasing progress; falls back to the
    // chunk-count estimate until the upload session exists.
    getUploadProgress(): number | null {
      let raw: number | null;
      if (!uploadRef) {
        raw =
          totalChunkCount === 0
            ? null
            : (confirmedChunkCount / totalChunkCount) * 100;
      } else {
        const confirmed = uploadRef.getConfirmedBytes();
        const pending = uploadRef.getPendingBytes();
        const total = confirmed + pending;
        raw = total === 0 ? null : (confirmed / total) * 100;
      }

      if (raw === null) return peakProgress;
      const clamped = Math.min(100, Math.max(0, raw));
      peakProgress =
        peakProgress === null ? clamped : Math.max(peakProgress, clamped);
      return peakProgress;
    },

    updateActivity() {
      inactivityClock.updateActivity();
    },

    getRemainingTime(): number | null {
      return inactivityClock.getRemainingTime();
    },

    isTimedOut(): boolean {
      return inactivityClock.isTimedOut();
    },

    // Reason-based coordination: several sources (timeout, screen-share mute) pause
    // independently; the recorder resumes only once ALL reasons are cleared.
    pauseRecording(reason?: string) {
      if (reason) {
        pauseReasons.add(reason);
      }
      if (recorder && recorder.state === 'recording') {
        // Flush buffered data first so everything up to the pause point is emitted.
        recorder.requestData();
        recorder.pause();
        pauseStartTime = Date.now();
        setState({ isPaused: true });
      }
    },

    resumeRecording(reason?: string) {
      if (reason) {
        pauseReasons.delete(reason);
        if (pauseReasons.size > 0) return;
      }
      if (recorder && recorder.state === 'paused') {
        recorder.resume();
        if (pauseStartTime) {
          pausedTime += Date.now() - pauseStartTime;
          pauseStartTime = null;
        }
        setState({ isPaused: false });
      }
    },

    setRecorder(
      nextRecorder: MediaRecorder,
      nextScreenStream: MediaStream,
      nextMicStream: MediaStream | null
    ) {
      recorder = nextRecorder;
      screenStream = nextScreenStream;
      micStream = nextMicStream;
    },
    getRecorder: () => recorder,
    getMicStream: () => micStream,

    setCameraStream(stream: MediaStream | null) {
      setState({ cameraStream: stream });
    },
    getCameraStream: () => snapshot.cameraStream,
    setCameraActive(active: boolean) {
      setState({ isCameraActive: active });
    },
    setCameraOn(on: boolean) {
      setState({ isCameraOn: on });
    },
    setCameraDeviceId(id: string | null) {
      setState({ cameraDeviceId: id });
    },
    setCameraError(err: CameraError | null) {
      setState({ cameraError: err });
    },
    beginCameraAcquire() {
      cameraGeneration += 1;
      return cameraGeneration;
    },
    getCameraGeneration: () => cameraGeneration,
    stopCameraTracks,

    setUpload(upload: ReturnType<typeof createResumableUpload>) {
      uploadRef = upload;
      flushBuffer();
    },

    addChunk(chunk: Blob, onUploadComplete?: (uploadedBytes?: number) => void) {
      totalChunkCount += 1;
      if (!uploadRef) {
        pendingChunks.push({ chunk, onUploadComplete });
        return;
      }
      void uploadRef.uploadChunk(chunk, onUploadComplete);
    },

    // onstop calls this before waitForUploads/finalize so trailing chunks that
    // were still queued locally reach the upload module.
    async drainBuffer(): Promise<void> {
      flushBuffer();
    },

    signalRecordingStopped() {
      if (uploadRef) {
        uploadRef.setRecordingStopped();
      }
    },

    finalize,

    finalizeSync() {
      if (uploadRef) {
        uploadRef.finalizeSync();
      }
    },

    async waitForUploads() {
      const upload = uploadRef;
      if (!upload) return;
      flushBuffer();
      return upload.waitForUploads();
    },

    cleanup,
    reset,

    stopRecording() {
      if (recorder && recorder.state === 'recording') {
        recorder.stop();
      } else {
        reset();
      }
    },

    stop() {
      if (recorder && recorder.state === 'recording') {
        recorder.stop();
      }
    },

    endRecording() {
      // A live recorder finalizes via its onstop handler; otherwise finalize now.
      // stop() is valid from both 'recording' and 'paused' — paused is the
      // timeout / hands-free play-pause path and still holds the unflushed tail,
      // so it must go through stop()/onstop/settle, not the fire-and-forget path.
      if (
        recorder &&
        (recorder.state === 'recording' || recorder.state === 'paused')
      ) {
        // Flush the partial trailing segment before stop — browsers can drop
        // media between the last timeslice boundary and stop().
        try {
          recorder.requestData();
        } catch {
          // Some browsers throw outside 'recording' state; safe to ignore.
        }
        recorder.stop();
      } else {
        void finalize();
        reset();
      }
    },
  };
}

// Module singleton: recorder state must outlive React remounts (double-recorder guard).
export const stateManager = createRecordingStateManager();
